import React from 'react';
import { Box } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { RoleSlug } from '@askporter/client-grieg-lyric';
import { AvatarSize } from '../Avatar';
import { Typography } from '../Typography';
import { UserAvatar } from '../UserAvatar';

/**
 * The card fill applied to the {@link Timeline}. Each variant maps to a tinted background and a matching
 * mid-tone for the connector that joins consecutive rows.
 */
export type TimelineVariant = 'default' | 'info' | 'success' | 'warning' | 'error';

/**
 * A single entry shown in the {@link Timeline}, representing something an actor did at a point in time.
 */
export type TimelineItem = {
  /**
   * Full label with the actor name already interpolated, e.g. "Quote sent by Lewis Morgan". Interpolation is the
   * caller's responsibility so the whole sentence can be translated as one string.
   */
  label: string;
  /**
   * The leading portion of `label` rendered semibold, e.g. "Quote sent". Must be a prefix of `label`; when it is
   * not, the label renders unemphasised rather than duplicating text.
   */
  emphasis?: string;
  /**
   * Pre-formatted timestamp, e.g. "02 Mar 26, 19:00". Formatting and localisation are the caller's responsibility.
   */
  date?: string;
  /**
   * optional - the actor's given name, used for the avatar initials
   */
  givenName?: string;
  /**
   * optional - the actor's family name, used for the avatar initials
   */
  familyName?: string;
  /**
   * optional - url path to the actor's profile photo
   */
  imagePath?: string;
  /**
   * optional - when present the actor's role icon is shown on the avatar
   */
  userRole?: RoleSlug;
};

export type TimelineProps = {
  /**
   * The ordered entries to display, newest first
   */
  items: Array<TimelineItem>;
  /**
   * Card fill. Defaults to 'default' (white with a border).
   */
  variant?: TimelineVariant;
  /**
   * When false only the first item renders and the connectors are hidden. Defaults to `false`.
   */
  isExpanded?: boolean;
  /**
   * Called when the expand affordance is activated. Omit to hide the expand affordance entirely (used when there
   * is only one item). The timeline is controlled — the caller owns `isExpanded` and toggles it here.
   */
  onToggle?: () => void;
  /**
   * Visible text for the expand affordance (translated by the caller). The affordance only renders when both
   * `onToggle` and `toggleLabel` are supplied, so the button always has an accessible name.
   */
  toggleLabel?: string;
  /**
   * Text shown in place of the list when `items` is empty (translated by the caller)
   */
  emptyText?: string;
  /**
   * Accessible label for the timeline list (translated by the caller)
   */
  ariaLabel?: string;
  /**
   * Test id applied to the timeline card
   */
  dataTestId?: string;
};

type TimelineVariantStyle = {
  backgroundColor: string;
  borderColor?: string;
  connectorColour: string;
};

/**
 * Fills come from the 50 step of each palette ramp; the connector uses the 300 step of the same ramp so it stays
 * legible against the tint without competing with the text.
 */
const VARIANT_STYLES: Record<TimelineVariant, TimelineVariantStyle> = {
  default: { backgroundColor: '#FFFFFF', borderColor: '#E9EAEB', connectorColour: '#D0D5DD' },
  info: { backgroundColor: '#EFF4FF', connectorColour: '#84ADFF' },
  success: { backgroundColor: '#EDFCF2', connectorColour: '#73E2A3' },
  warning: { backgroundColor: '#FFFAEB', connectorColour: '#FEC84B' },
  error: { backgroundColor: '#FEF3F2', connectorColour: '#FDA29B' },
};

const AVATAR_SIZE = 32;
const CONNECTOR_WIDTH = 2;
const TEXT_LINE_HEIGHT = 20;
const CONNECTOR_INSET = 4;
const CONNECTOR_LENGTH = 34;
// The connector sets the row rhythm, not the text: a dated row is only 40px of text, so without this the rows
// close up and the connector is squeezed to a stub.
const CONNECTED_ROW_HEIGHT = AVATAR_SIZE + CONNECTOR_INSET + CONNECTOR_LENGTH + CONNECTOR_INSET;

const LABEL_COLOUR = '#181D27';
const DATE_COLOUR = '#414651';
const TOGGLE_COLOUR = '#414651';

const TEXT_SX = { fontSize: '14px', lineHeight: `${TEXT_LINE_HEIGHT}px` } as const;

/**
 * Vertical activity timeline. Each row pairs an actor's avatar with a label — optionally split into a semibold
 * lead-in and the rest of the sentence — and a pre-formatted timestamp. When more than one entry exists the
 * caller supplies `onToggle` so the list can be collapsed down to its most recent entry; the affordance always
 * reports its state through `aria-expanded` regardless of the visible label, so a static label such as
 * "Show timeline" stays accurate to assistive technology.
 */
export const Timeline: React.FC<TimelineProps> = ({
  items,
  variant = 'default',
  isExpanded = false,
  onToggle,
  toggleLabel,
  emptyText,
  ariaLabel,
  dataTestId = 'timeline',
}) => {
  const listId = React.useId();
  const { backgroundColor, borderColor, connectorColour } = VARIANT_STYLES[variant];

  const isEmpty = items.length === 0;
  const visibleItems = isExpanded ? items : items.slice(0, 1);
  const hasToggle = Boolean(onToggle) && Boolean(toggleLabel) && !isEmpty;

  // The toggle centres against the leading row: two lines of text when dated, otherwise just the avatar
  const leadingRowHeight = items[0]?.date ? TEXT_LINE_HEIGHT * 2 : AVATAR_SIZE;

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        borderRadius: '12px',
        p: '16px',
        backgroundColor,
        border: borderColor ? `1px solid ${borderColor}` : undefined,
      }}
    >
      {isEmpty ? (
        <Typography variant="body2" sx={{ ...TEXT_SX, color: DATE_COLOUR }}>
          {emptyText}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <Box
            component="ol"
            id={listId}
            aria-label={ariaLabel}
            sx={{ flex: 1, minWidth: 0, listStyle: 'none', p: 0, m: 0 }}
          >
            {visibleItems.map((item, index) => {
              const { label, emphasis, date, givenName, familyName, imagePath, userRole } = item;
              const emphasisPrefix = emphasis && label.startsWith(emphasis) ? emphasis : undefined;
              const hasConnector = isExpanded && index < visibleItems.length - 1;

              return (
                <Box
                  component="li"
                  // The index leads the key so two entries with the same label and timestamp — a manager
                  // resending twice within the same minute — stay distinct rows rather than collapsing into one.
                  key={`${index}-${label}-${date ?? ''}`}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    minHeight: hasConnector ? `${CONNECTED_ROW_HEIGHT}px` : undefined,
                  }}
                >
                  <Box sx={{ flexShrink: 0, width: `${AVATAR_SIZE}px`, height: `${AVATAR_SIZE}px` }}>
                    <UserAvatar
                      givenName={givenName}
                      familyName={familyName}
                      imagePath={imagePath}
                      userRole={userRole}
                      avatarSize={AvatarSize.SM}
                      dataTestId={`${dataTestId}-avatar-${index}`}
                    />
                  </Box>
                  {hasConnector && (
                    <Box
                      aria-hidden="true"
                      sx={{
                        position: 'absolute',
                        top: `${AVATAR_SIZE + CONNECTOR_INSET}px`,
                        bottom: `${CONNECTOR_INSET}px`,
                        left: `${AVATAR_SIZE / 2 - CONNECTOR_WIDTH / 2}px`,
                        width: `${CONNECTOR_WIDTH}px`,
                        backgroundColor: connectorColour,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      // Two lines of text overflow this minimum and sit top-aligned; a row with no date is a
                      // single line, which centres against the avatar rather than hanging off the top of it.
                      justifyContent: 'center',
                      minHeight: `${AVATAR_SIZE}px`,
                    }}
                  >
                    <Typography variant="body2" sx={{ ...TEXT_SX, fontWeight: 500, color: LABEL_COLOUR }}>
                      {emphasisPrefix ? (
                        <>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {emphasisPrefix}
                          </Box>
                          {label.slice(emphasisPrefix.length)}
                        </>
                      ) : (
                        label
                      )}
                    </Typography>
                    {date && (
                      <Typography variant="body2" sx={{ ...TEXT_SX, fontWeight: 400, color: DATE_COLOUR }}>
                        {date}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {hasToggle && (
            <Box
              component="button"
              type="button"
              onClick={onToggle}
              aria-expanded={isExpanded}
              aria-controls={listId}
              sx={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                appearance: 'none',
                background: 'none',
                border: 0,
                p: 0,
                m: 0,
                height: `${leadingRowHeight}px`,
                // A bare <button> does not inherit the body font — without this it renders in the browser default
                fontFamily: ({ typography }) => typography.fontFamily,
                ...TEXT_SX,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: TOGGLE_COLOUR,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                  borderRadius: '4px',
                },
              }}
            >
              {toggleLabel}
              <Box
                aria-hidden="true"
                sx={{
                  display: 'flex',
                  transform: isExpanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms ease',
                }}
              >
                <ChevronDown size={24} strokeWidth={1.5} color={TOGGLE_COLOUR} absoluteStrokeWidth />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
